import React, { useState, useEffect } from 'react';
import './CompanyInfo.css';
import iconBack from '../../assets/images/icon-back.svg';
import { supabase } from '../../lib/supabase';

interface CompanyInfoProps {
  onBack: () => void;
}

interface AppSettings {
  [key: string]: string;
}

const CompanyInfo: React.FC<CompanyInfoProps> = ({ onBack }) => {
  const [settings, setSettings] = useState<AppSettings>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('setting_key, setting_value')
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching settings:', error);
          return;
        }

        // key-value 형태로 변환
        const settingsMap: AppSettings = {};
        data?.forEach((item: { setting_key: string; setting_value: string }) => {
          settingsMap[item.setting_key] = item.setting_value;
        });

        setSettings(settingsMap);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSocialClick = (url: string | undefined) => {
    if (url && url !== 'EMPTY' && url.trim() !== '') {
      window.open(url, '_blank');
    }
  };

  const hasSocialLink = (url: string | undefined) => {
    return url && url !== 'EMPTY' && url.trim() !== '';
  };

  if (loading) {
    return (
      <div className="company-info-container">
        <div className="company-info-header">
          <div className="company-info-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
            <h1 className="company-info-title">회사 정보</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="company-info-content">
          <div className="loading">로딩 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-info-container">
      <div className="company-info-header">
        <div className="company-info-header-wrapper">
          <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="company-info-title">회사 정보</h1>
          <div className="header-icon"></div>
        </div>
      </div>

      <div className="company-info-content">
        <h2 className="company-info-page-title">회사 정보</h2>
        <div className="info-section">
          <h2 className="section-title">INFO.</h2>

          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">회사명</span>
              <span className="info-value">{settings.company_name || '-'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">대표</span>
              <span className="info-value">{settings.ceo_name || '-'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">사업자등록번호</span>
              <span className="info-value">{settings.business_number || '-'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">통신판매업</span>
              <span className="info-value">{settings.telecom_sales_number || '-'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">주소</span>
              <span className="info-value">{settings.address || '-'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">광고·제휴문의</span>
              <span className="info-value">{settings.contact_email || '-'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">고객센터</span>
              <span className="info-value">{settings.customer_center || '-'}</span>
            </div>
          </div>
        </div>

        <div className="social-section">
          <h3 className="social-title">SNS 채널</h3>
          <div className="social-buttons">
            <button
              className={`social-button ${!hasSocialLink(settings.sns_youtube) ? 'disabled' : ''}`}
              onClick={() => handleSocialClick(settings.sns_youtube)}
              disabled={!hasSocialLink(settings.sns_youtube)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M23.5 6.19c-.28-1.04-1.09-1.86-2.14-2.14C19.49 3.6 12 3.6 12 3.6s-7.49 0-9.36.45c-1.05.28-1.86 1.1-2.14 2.14C.06 8.05 0 10.01 0 12s.06 3.95.5 5.81c.28 1.04 1.09 1.86 2.14 2.14 1.87.45 9.36.45 9.36.45s7.49 0 9.36-.45c1.05-.28 1.86-1.1 2.14-2.14.44-1.86.5-3.82.5-5.81s-.06-3.95-.5-5.81zM9.6 15.6V8.4l6.24 3.6-6.24 3.6z" fill="#FF0000"/>
              </svg>
              <span>YouTube</span>
            </button>

            <button
              className={`social-button ${!hasSocialLink(settings.sns_naver_blog) ? 'disabled' : ''}`}
              onClick={() => handleSocialClick(settings.sns_naver_blog)}
              disabled={!hasSocialLink(settings.sns_naver_blog)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" rx="3" fill="#03C75A"/>
                <path d="M16.13 6h-8.25v12h3.94v-5l4.31 5h3.94V6h-3.94v5L16.13 6z" fill="white"/>
              </svg>
              <span>Naver Blog</span>
            </button>

            <button
              className={`social-button ${!hasSocialLink(settings.sns_instagram) ? 'disabled' : ''}`}
              onClick={() => handleSocialClick(settings.sns_instagram)}
              disabled={!hasSocialLink(settings.sns_instagram)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <defs>
                  <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FED373"/>
                    <stop offset="50%" stopColor="#E62B72"/>
                    <stop offset="100%" stopColor="#8F3FB8"/>
                  </linearGradient>
                </defs>
                <rect width="24" height="24" rx="6" fill="url(#instagram-gradient)"/>
                <circle cx="12" cy="12" r="3.75" stroke="white" strokeWidth="1.5" fill="none"/>
                <circle cx="17.25" cy="6.75" r="1.125" fill="white"/>
                <rect x="6" y="6" width="12" height="12" rx="3" stroke="white" strokeWidth="1.5" fill="none"/>
              </svg>
              <span>Instagram</span>
            </button>
          </div>
        </div>

        <div className="social-section">
          <h3 className="social-title">사전승낙서</h3>
          <div className="social-buttons">
            <button
              className="social-button"
              onClick={() => window.open('https://ictmarket.or.kr:8443/precon/pop_CertIcon.do?PRECON_REQ_ID=PRE0000190897', '_blank')}
            >
              <span>유선 사전승낙서</span>
            </button>

            <button
              className="social-button"
              onClick={() => window.open('https://ictmarket.or.kr:8443/precon/pop_CertIcon.do?PRECON_REQ_ID=PRE0000160340', '_blank')}
            >
              <span>무선 사전승낙서</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfo;
